/*
 * Copyright (c) Nexign, JSC, 2021-2024.
 */
/**
 * Account Info Discounts Component
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @author Viacheslav Smalenski
 * @version 1.29.0
 * @since 1.11.0
 */

import { Fragment, useEffect } from 'react';

import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { ImgStatusError } from '@components/common/Block/Img';
import {
    makeSelectPageAccountInfoTranslation,
    makeSelectTranslationCommon,
} from '@modules/internationalization/intSelectors';
import { Loader } from '@modules/loader/components/Loader';
import { IRouteParams } from '@routes/constants/routesConstants';
import { icons } from '@theme/index';
import { formatDate } from '@utils/formatText';
import { stringFormatParams } from '@utils/stringFormat';

import ErrorPage from '../../ErrorPage/ErrorPage';
import { accountInfoApi } from '../store';
import {
    AccountInfoRowBodyStyled,
    AccountInfoRowOnTimeStyled,
    AccountInfoRowStyled,
    AccountInfoRowTitleStyled,
} from './styled/AccountInfoFinancialStyled';
import { PageAccountInfoEmptyStyled, PageAccountInfoTitleStyled } from './styled/PageAccountInfoStyled';

const AccountInfoFinancialDiscounts = () => {
    const translationAccountInfo = useSelector(makeSelectPageAccountInfoTranslation());
    const translationCommon = useSelector(makeSelectTranslationCommon());

    const [
        loadDiscountsList,
        {
            data: discountsList,
            error: discountsListError,
            isError: discountsListIsError,
            isLoading: discountsListIsLoading,
        },
    ] = accountInfoApi.endpoints.getAccountInfoFinancesDiscounts.useLazyQuery();

    const translation = translationAccountInfo.financialInfo.discounts;

    const { clientId } = useParams<IRouteParams>();

    useEffect(() => {
        loadDiscountsList({ clientId });
    }, [clientId, loadDiscountsList]);

    if (discountsListIsError) {
        const error = translationCommon.error.codes[discountsListError?.response?.code ?? ''];
        return (
            <ErrorPage
                src={icons.errorDeliveryImage3x}
                text={error || translationCommon.error.ui.text}
                title={translationCommon.error.ui.title}
            />
        );
    }

    return (
        <Fragment>
            <Loader showLoader={discountsListIsLoading}>
                <PageAccountInfoTitleStyled>{translation.title}</PageAccountInfoTitleStyled>
                {!discountsList?.discounts?.length && !discountsListIsLoading && (
                    <PageAccountInfoEmptyStyled data-name="account-info-finance-no-discounts">
                        <ImgStatusError />
                        <div className="page-account-info__text">{translation.noneDiscounts}</div>
                    </PageAccountInfoEmptyStyled>
                )}
                {discountsList?.discounts?.map((item) => (
                    <Fragment key={item.discountId}>
                        <AccountInfoRowStyled>
                            <AccountInfoRowTitleStyled data-name="account-info-finance-discounts">
                                {item.name}
                            </AccountInfoRowTitleStyled>
                            <AccountInfoRowBodyStyled>
                                <p>
                                    {`${translationAccountInfo.financialInfo.discounts.restVolume} ${item.volume} ${item.measure}`}
                                </p>
                                {item.infinite ? (
                                    <p>
                                        {`${translationAccountInfo.financialInfo.discounts.spentVolume} ${item.spend} ${item.measureSpend}`}
                                    </p>
                                ) : (
                                    <p>
                                        {`${translationAccountInfo.financialInfo.discounts.total} ${item.total} ${item.measureTotal}`}
                                    </p>
                                )}
                                {item.unlimited && (
                                    <AccountInfoRowOnTimeStyled>
                                        {translationAccountInfo.financialInfo.discounts.unlimited}
                                    </AccountInfoRowOnTimeStyled>
                                )}
                                {!item.unlimited && item.endDate && (
                                    <AccountInfoRowOnTimeStyled>
                                        {stringFormatParams(translationAccountInfo.financialInfo.discounts.untilDate, {
                                            date: formatDate(item.endDate),
                                        })}
                                    </AccountInfoRowOnTimeStyled>
                                )}
                            </AccountInfoRowBodyStyled>
                        </AccountInfoRowStyled>
                    </Fragment>
                ))}
            </Loader>
        </Fragment>
    );
};

export default AccountInfoFinancialDiscounts;
